(() => {
  const mounted = new WeakSet();
  let internalMutations = 0;
  let refreshTimer;
  const pathMatches = (path, rule) => {
    const prefix = String(rule || '').trim().replace(/\/$/, '') || '';
    if (!prefix) return false;
    const unlocalized = path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '') || '/';
    return [path, unlocalized].some(value => value === prefix || value.startsWith(prefix + '/'));
  };
  const applyRules = root => {
    const script = root.querySelector('[data-concept-one-config]');
    if (!script) return;
    let config;
    try { config = JSON.parse(script.textContent); } catch { return; }
    const rule = config.rules.find(rule => pathMatches(location.pathname, rule.path));
    const offer = root.querySelector('[data-concept-offer]');
    if (offer) {
      const visible = config.count > 0 && config.offerEnabled !== false && !rule?.hideOffer;
      offer.classList.toggle('cp-hidden', !visible);
      const html = rule?.message || null;
      if (html !== null) {
        if (offer.innerHTML !== html) offer.innerHTML = html;
      } else if (offer.textContent !== offer.dataset.baseMessage) offer.textContent = offer.dataset.baseMessage;
    }
    const shopping = root.querySelector('[data-concept-shopping]');
    if (shopping) shopping.classList.toggle('cp-hidden', !!rule?.hideShopping);
  };
  const refresh = () => {
    clearTimeout(refreshTimer);
    const requestedAt = Date.now();
    refreshTimer = setTimeout(async () => {
      const root = document.querySelector('[data-concept-one]');
      const drawer = root?.closest('ds-cart-drawer');
      if (!drawer || !drawer.isOpen || internalMutations > 0) return;
      if (drawer.cartRenderedAt >= requestedAt) return;
      if (drawer.loading) { refresh(); return; }
      try { await drawer.refreshHeaderCartSafe(); } catch { /* Existing drawer retains its last successful render. */ }
    }, 180);
  };
  const mount = root => {
    if (mounted.has(root)) return;
    mounted.add(root);
    applyRules(root);
    let pending;
    let previousFocus;
    let wasOpen = root.getAttribute('aria-hidden') === 'false';
    const observer = new MutationObserver(records => {
      if (!records.some(record => record.type === 'childList' || record.target === root)) return;
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(() => {
        if (!root.isConnected) { observer.disconnect(); return; }
        applyRules(root);
        const isOpen = root.getAttribute('aria-hidden') === 'false';
        if (isOpen && !wasOpen) {
          previousFocus = document.activeElement;
          root.querySelector('.cp-close')?.focus({preventScroll: true});
        } else if (!isOpen && wasOpen && previousFocus?.isConnected) previousFocus.focus({preventScroll: true});
        wasOpen = isOpen;
      });
    });
    observer.observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden']});
    root.addEventListener('keydown', event => {
      if (event.key !== 'Tab' || root.getAttribute('aria-hidden') === 'true') return;
      const focusable = [...root.querySelectorAll('a[href],button:not([disabled]),[tabindex="0"]')].filter(el => el.getClientRects().length && !el.closest('.cp-hidden'));
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    root.addEventListener('click', async event => {
      const button = event.target.closest('[data-cart-qty],[data-cart-remove]');
      if (!button || !root.contains(button) || !button.dataset.groupItems) return;
      event.preventDefault();
      event.stopPropagation();
      const drawer = root.closest('ds-cart-drawer');
      if (!drawer || drawer.loading || internalMutations > 0) return;
      let items;
      try { items = JSON.parse(button.dataset.groupItems); } catch { return; }
      if (!Array.isArray(items) || !items.length) return;
      const quantity = button.hasAttribute('data-cart-remove') ? 0 : Number(button.dataset.qty);
      if (!Number.isInteger(quantity) || quantity < 0) return;
      const updates = {};
      let remaining = quantity;
      for (const [index, item] of items.entries()) {
        if (typeof item.key !== 'string' || !Number.isInteger(item.qty) || item.qty < 0) return;
        const allocated = index === items.length - 1 ? remaining : Math.min(item.qty, remaining);
        const keep = Number.isInteger(item.removeTo) && item.removeTo > 0 ? item.removeTo : 0;
        updates[item.key] = allocated + keep;
        remaining -= allocated;
      }
      internalMutations++;
      drawer.loading = true;
      drawer.setLoading(true);
      const error = root.querySelector('[data-concept-error]');
      if (error) error.classList.add('cp-hidden');
      try {
        const base = window.Shopify?.routes?.root || '/';
        const response = await fetch(base + 'cart/update.js', {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({updates})});
        if (!response.ok) throw new Error('Cart update failed');
        await drawer.refreshHeaderCartSafe();
      } catch {
        if (error) error.classList.remove('cp-hidden');
      } finally {
        internalMutations--;
        drawer.loading = false;
        drawer.clearCartMutationOverlay?.();
      }
    }, true);
  };
  const init = () => document.querySelectorAll('[data-concept-one]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true}); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:updated', refresh);
  document.addEventListener('cart:refresh', refresh);
  if (!window.__dsCartFetchBridgeInstalled && !window.__conceptOneMutationBridge) {
    window.__conceptOneMutationBridge = true;
    const nativeFetch = window.fetch;
    window.fetch = async function(...args) {
      const response = await nativeFetch.apply(this, args);
      const request = args[0];
      const method = String(args[1]?.method || request?.method || 'GET').toUpperCase();
      const url = typeof request === 'string' ? request : request?.url;
      if (!window.__dsCartFetchBridgeInstalled && response.ok && method === 'POST' && /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(String(url)) && internalMutations === 0 && document.querySelector('[data-concept-one]')) refresh();
      return response;
    };
  }
})();

